import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRight,
    BarChart3,
    Calendar,
    Clock,
    Loader2,
    Receipt,
    RefreshCw,
    TrendingDown,
    TrendingUp,
    Wallet,
} from "lucide-react";
import ChartBox from "./charts/ChartBox";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { odemeTakipOzet } from "../lib/api";

const AYLAR = [
    { value: 0, label: "Tüm yıl" },
    { value: 1, label: "Oca" },
    { value: 2, label: "Şub" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Nis" },
    { value: 5, label: "May" },
    { value: 6, label: "Haz" },
    { value: 7, label: "Tem" },
    { value: 8, label: "Ağu" },
    { value: 9, label: "Eyl" },
    { value: 10, label: "Eki" },
    { value: 11, label: "Kas" },
    { value: 12, label: "Ara" },
];

const YILLAR = [2023, 2024, 2025, 2026, 2027];

const UYARI_STIL = {
    warning: "border-amber-500/35 bg-amber-500/10 text-amber-100",
    danger: "border-rose-500/35 bg-rose-500/10 text-rose-100",
    info: "border-sky-500/35 bg-sky-500/10 text-sky-100",
};

function fmt(n) {
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n || 0);
}

function fmtShort(n) {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
    return String(Math.round(v));
}

function KpiCard({ label, value, sub, accent, icon: Icon }) {
    const border =
        accent === "emerald"
            ? "border-emerald-500/30 bg-emerald-500/8"
            : accent === "rose"
              ? "border-rose-500/30 bg-rose-500/8"
              : accent === "gold"
                ? "border-[#D4AF37]/35 bg-[#D4AF37]/8"
                : accent === "violet"
                  ? "border-violet-500/30 bg-violet-500/8"
                  : "border-white/10 bg-[#0B0F19]/60";
    const text =
        accent === "emerald"
            ? "text-emerald-300"
            : accent === "rose"
              ? "text-rose-300"
              : accent === "gold"
                ? "text-[#F3E5AB]"
                : accent === "violet"
                  ? "text-violet-300"
                  : "text-white";

    return (
        <div className={`rounded-xl border p-4 ${border}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {label}
                </div>
                {Icon && (
                    <Icon className="h-4 w-4 text-slate-500 shrink-0 opacity-70" />
                )}
            </div>
            <div className={`text-xl md:text-2xl font-semibold mt-1 ${text}`}>
                {value}
            </div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function OdemeTakipPanel({ period, onPeriodChange, onRefresh, listCount }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        const params = { yil: period.yil };
        if (period.ay) params.ay = period.ay;
        odemeTakipOzet(params)
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [period.yil, period.ay]);

    useEffect(() => {
        load();
    }, [load]);

    const chartData = useMemo(() => data?.aylik || [], [data]);

    const handleMonth = (ay) => {
        onPeriodChange({ ...period, ay });
    };

    const handleYear = (yil) => {
        onPeriodChange({ yil: Number(yil), ay: period.ay });
    };

    const uyarilar = data?.uyarilar || [];

    return (
        <section className="relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-[#121A2F] via-[#0f1424] to-[#0d1a18] p-5 md:p-7 mb-6 shadow-[0_0_60px_rgba(16,185,129,0.06)]">
            <div
                className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-emerald-600/12 blur-3xl"
                aria-hidden
            />
            <div
                className="pointer-events-none absolute -bottom-20 -left-12 h-48 w-48 rounded-full bg-[#D4AF37]/10 blur-3xl"
                aria-hidden
            />

            <div className="relative">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-emerald-200 mb-2">
                            <Wallet className="h-3.5 w-3.5 text-[#D4AF37]" />
                            Ödeme Takip Merkezi
                        </div>
                        <h2 className="font-display text-xl md:text-2xl text-white tracking-tight">
                            {data?.ay_label || "Özet"} · {period.yil}
                            {listCount != null && (
                                <span className="ml-2 text-sm font-mono font-normal text-slate-400 border border-white/10 rounded-lg px-2 py-0.5">
                                    {listCount} kayıt listede
                                </span>
                            )}
                        </h2>
                        <p className="text-xs text-slate-500 mt-1 max-w-xl">
                            Gelen/giden ödemeler, masraflar ve hakedişler — ay ve yıl filtresi
                            tabloyu da günceller.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <select
                            value={period.yil}
                            onChange={(e) => handleYear(e.target.value)}
                            className="h-9 bg-[#0B0F19] border border-white/10 rounded-lg px-3 text-sm text-slate-200"
                        >
                            {YILLAR.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => {
                                load();
                                onRefresh?.();
                            }}
                            disabled={loading}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-sm text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <RefreshCw className="h-4 w-4" />
                            )}
                            Yenile
                        </button>
                        <Link
                            to="/yeni/odeme-raporlari"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 text-sm text-slate-300 hover:bg-white/5"
                        >
                            <BarChart3 className="h-4 w-4" />
                            Raporlar
                            <ArrowRight className="h-3.5 w-3.5 opacity-60" />
                        </Link>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                    {AYLAR.map((a) => {
                        const active = period.ay === a.value;
                        return (
                            <button
                                key={a.value}
                                type="button"
                                onClick={() => handleMonth(a.value)}
                                className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                                    active
                                        ? "bg-emerald-600/80 text-white border border-emerald-500/50"
                                        : "border border-white/10 text-slate-400 hover:bg-white/5 hover:text-slate-200"
                                }`}
                            >
                                {a.label}
                            </button>
                        );
                    })}
                </div>

                {loading && !data ? (
                    <div className="flex items-center gap-2 text-slate-400 py-8">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Özet yükleniyor…
                    </div>
                ) : (
                    <>
                        {uyarilar.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-5">
                                {uyarilar.map((u, i) => (
                                    <div
                                        key={i}
                                        className={`rounded-xl border px-4 py-3 flex gap-3 ${UYARI_STIL[u.seviye] || UYARI_STIL.info}`}
                                    >
                                        {u.ikon === "alert" ? (
                                            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                                        ) : u.ikon === "clock" ? (
                                            <Clock className="h-5 w-5 shrink-0 mt-0.5" />
                                        ) : u.ikon === "calendar" ? (
                                            <Calendar className="h-5 w-5 shrink-0 mt-0.5" />
                                        ) : (
                                            <Receipt className="h-5 w-5 shrink-0 mt-0.5" />
                                        )}
                                        <div>
                                            <div className="text-sm font-semibold">{u.baslik}</div>
                                            <p className="text-xs opacity-85 mt-0.5 leading-relaxed">
                                                {u.aciklama}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
                            <KpiCard
                                label="Gelen ödeme"
                                value={fmt(data?.toplam_gelen)}
                                accent="emerald"
                                icon={TrendingUp}
                            />
                            <KpiCard
                                label="Ön ödeme / giden"
                                value={fmt(data?.toplam_giden)}
                                accent="rose"
                                icon={TrendingDown}
                            />
                            <KpiCard
                                label="Toplam masraf"
                                value={fmt(data?.toplam_masraf)}
                                sub={`${data?.kayit_sayisi || 0} kayıt`}
                                accent="gold"
                                icon={Receipt}
                            />
                            <KpiCard
                                label="Sigortalı hakediş"
                                value={fmt(data?.sigortali_hakedis_toplam)}
                                accent="violet"
                            />
                            <KpiCard
                                label="MBS hakediş"
                                value={fmt(data?.mbs_hakedis_toplam)}
                                accent="gold"
                            />
                            <KpiCard
                                label="Bekleyen"
                                value={fmt(data?.bekleyen_tutar)}
                                sub={`${data?.bekleyen_sayisi || 0} ödenmedi`}
                                accent="rose"
                                icon={Clock}
                            />
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <div className="xl:col-span-2 rounded-xl border border-white/10 bg-[#0B0F19]/50 p-4 min-h-[17rem]">
                                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                                    {period.ay ? `${data?.ay_label} detayı` : `${period.yil} aylık özet`}
                                </h3>
                                <ChartBox height={220} debounce={48}>
                                    <BarChart
                                        data={
                                            period.ay
                                                ? chartData.filter((d) => d.ay === period.ay)
                                                : chartData
                                        }
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                        <XAxis
                                            dataKey="ay_label"
                                            tick={{ fill: "#94a3b8", fontSize: 10 }}
                                        />
                                        <YAxis
                                            tick={{ fill: "#94a3b8", fontSize: 10 }}
                                            tickFormatter={fmtShort}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                background: "#0B0F19",
                                                border: "1px solid #334155",
                                                borderRadius: 8,
                                                fontSize: 12,
                                            }}
                                            formatter={(v) => fmt(v)}
                                        />
                                        <Bar
                                            dataKey="gelen"
                                            name="Gelen"
                                            fill="#34D399"
                                            radius={[3, 3, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="mbs_hakedis"
                                            name="MBS hakediş"
                                            fill="#D4AF37"
                                            radius={[3, 3, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="masraf"
                                            name="Masraf"
                                            fill="#F472B6"
                                            radius={[3, 3, 0, 0]}
                                        />
                                    </BarChart>
                                </ChartBox>
                            </div>

                            <div className="space-y-4">
                                {(data?.masraf_kalemleri || []).length > 0 && (
                                    <div className="rounded-xl border border-white/10 bg-[#0B0F19]/50 p-4">
                                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                                            Masraf kalemleri
                                        </h3>
                                        <ul className="space-y-2">
                                            {(data.masraf_kalemleri || []).map((m, i) => (
                                                <li
                                                    key={i}
                                                    className="flex justify-between text-sm gap-2"
                                                >
                                                    <span className="text-slate-300 truncate">
                                                        {m.tur}
                                                        <span className="text-slate-600 ml-1">
                                                            ×{m.adet}
                                                        </span>
                                                    </span>
                                                    <span className="text-[#F3E5AB] font-mono text-xs shrink-0">
                                                        {fmt(m.toplam)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {(data?.bekleyen_liste || []).length > 0 && (
                                    <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4">
                                        <h3 className="text-xs font-semibold text-amber-300/90 uppercase tracking-wider mb-3">
                                            Bekleyen ödemeler
                                        </h3>
                                        <ul className="space-y-2">
                                            {data.bekleyen_liste.map((b) => (
                                                <li
                                                    key={b.id}
                                                    className="text-xs border-b border-white/5 pb-2 last:border-0 last:pb-0"
                                                >
                                                    <div className="text-slate-200 font-medium">
                                                        {b.dosya_no || `#${b.id}`}
                                                    </div>
                                                    <div className="flex justify-between text-slate-500 mt-0.5">
                                                        <span className="truncate">{b.muvekkil}</span>
                                                        <span className="text-amber-200 font-mono shrink-0 ml-2">
                                                            {fmt(b.odeme_miktari)}
                                                        </span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
